import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home, Briefcase, Building2, Users, Mail, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { TopBar } from './TopBar';
import { Button } from '@/components/ui/button';
import { DynamicNavigation } from '../lightswind/dynamic-navigation';
import logoSrc from '@/assets/lg4.webp';

const navLinks = [
  { id: 'home', label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
  { id: 'services', label: 'Our Services', href: '/services', icon: <Briefcase className="w-4 h-4" /> },
  { id: 'projects', label: 'Our Projects', href: '/portfolio', icon: <Building2 className="w-4 h-4" /> },
  { id: 'about', label: 'Build With Us', href: '/about', icon: <Users className="w-4 h-4" /> },
  { id: 'contact', label: 'Contact', href: '/contact', icon: <Phone className="w-4 h-4" /> },
];

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  return (
    <div className="flex flex-col">
      <div className="sticky top-0 left-0 right-0 z-50">
        <Link
          to="/"
          className="hidden xl:flex absolute left-4 top-2 z-60 h-32 w-32 overflow-hidden bg-[#f8f9fb] border border-slate-200 shadow-md"
        >
          <img
            src={logoSrc}
            alt="AST"
            className="h-full w-full object-cover"
          />
        </Link>
        <div className="hidden xl:block">
          <TopBar />
        </div>
        {/* Logo + Nav combined row */}
        <motion.header
          className="bg-background/80 backdrop-blur-md border-b border-foreground/5 shadow-lg"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
        >
          <div className="container-custom">
            <div className="flex items-center justify-between min-h-20 xl:min-h-24 py-2">

              {/* Logo + Company name — always visible together */}
              <div className="flex items-center gap-2 sm:gap-3 shrink-0 xl:ml-20 min-w-0">
                {/* Logo */}
                <Link to="/" className="flex items-center shrink-0 xl:hidden">
                  <img
                    src={logoSrc}
                    alt="AST"
                    className="h-9 sm:h-10 w-auto object-contain transition-all duration-300"
                  />
                </Link>

                {/* Company name - responsive text sizing */}
                <div className="flex flex-col leading-none min-w-0">
                  <p className="font-black tracking-wider sm:tracking-widest uppercase text-orange-500 text-base sm:text-lg md:text-xl leading-[1.1]">
                    AST
                  </p>
                  <p className="font-bold tracking-wide sm:tracking-wider uppercase text-slate-900 text-[10px] sm:text-xs md:text-sm leading-[1.2] hidden sm:block">
                    Constructions Pvt Ltd
                  </p>
                  <p className="font-medium text-slate-400 uppercase text-[8px] sm:text-[10px] tracking-wider hidden md:block">
                    Build Your Future
                  </p>
                </div>
              </div>

              <div className="hidden xl:flex flex-1 justify-center">
                <DynamicNavigation
                  links={navLinks}
                  theme="dark"
                  glowIntensity={2}
                  className="px-1 md:px-2 lg:px-4 py-0"
                  cta={
                    <Button
                      onClick={() => navigate('/contact')}
                      className="bg-accent text-slate-900 hover:bg-amber-400 font-bold px-3 md:px-3.5 lg:px-4 py-1.5 lg:py-2 rounded-full transition-all shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 tracking-tight text-xs lg:text-sm h-auto"
                    >
                      Start New Project
                    </Button>
                  }
                />
              </div>

              {/* Phone + Hamburger group — right side on small screens */}
              <div className="xl:hidden flex items-center gap-2 sm:gap-3 shrink-0">
                {/* Phone icon only on small screens, with number on larger mobile */}
                <a
                  href="tel:0750441260"
                  className="flex items-center gap-1.5 sm:gap-2 pr-2 sm:pr-3 border-r border-slate-200"
                >
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 shrink-0" />
                  <div className="leading-tight hidden sm:block">
                    <p className="text-[9px] text-[#7A9BAD] uppercase tracking-wide">Call / WhatsApp</p>
                    <p className="text-xs font-semibold text-slate-800 whitespace-nowrap">0750 441 260</p>
                  </div>
                </a>
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 text-foreground hover:text-primary transition-colors"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>

            </div>
          </div>
        </motion.header>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="xl:hidden bg-white border-b border-slate-200 shadow-xl"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: 'easeInOut' }}
            >
              <div className="container-custom py-4">
                <div className="flex flex-col gap-1">
                  {navLinks.map((link) => {
                    const isActive = location.pathname === link.href;
                    return (
                      <Link
                        key={link.id}
                        to={link.href}
                        className={`flex items-center gap-3 px-3 py-3 rounded-xl font-black uppercase tracking-wider text-sm transition-colors ${
                          isActive
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="shrink-0">{link.icon}</span>
                        <span>{link.label}</span>
                      </Link>
                    );
                  })}
                </div>

                <button
                  onClick={() => {
                    navigate('/portfolio');
                    setIsMobileMenuOpen(false);
                  }}
                  className="mt-4 w-full px-4 py-3 bg-accent text-slate-900 hover:bg-amber-400 rounded-xl uppercase font-black tracking-wider transition-all"
                >
                  Start New Project
                </button>

                <div className="mt-4 rounded-xl overflow-hidden" style={{ backgroundColor: '#08122B' }}>
                  <a
                    href="mailto:astconst.14@gmail.com"
                    className="flex items-center gap-3 px-3 py-3 border-b border-[#D4AF37]/25"
                  >
                    <Mail className="w-4 h-4 shrink-0" color="#D4AF37" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Email</p>
                      <p className="text-sm font-semibold text-white truncate">astconst.14@gmail.com</p>
                    </div>
                  </a>
                  <a
                    href="tel:0750441260"
                    className="flex items-center gap-3 px-3 py-3"
                  >
                    <Phone className="w-4 h-4 shrink-0" color="#D4AF37" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Call / WhatsApp</p>
                      <p className="text-sm font-semibold text-white">0750441260</p>
                    </div>
                  </a>
                  <a
                    href="tel:0212228130"
                    className="flex items-center gap-3 px-3 py-3 border-t border-[#D4AF37]/25"
                  >
                    <Phone className="w-4 h-4 shrink-0" color="#D4AF37" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Landline</p>
                      <p className="text-sm font-semibold text-white">0212228130</p>
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
